import React, { useState } from 'react';
import { Loader2, Lock, RotateCcw, Save } from 'lucide-react';
import { systemService } from '../../services/systemService';
import { useCatalog, sameSet } from '../../lib/permissions';
import PermissionPicker, { MenuPreview } from '../roles/PermissionPicker';

const UserPermissionsTab = ({ user, canEdit, onSaved }) => {
    const { catalog, error: catalogError } = useCatalog();
    const baseline = user.rolePermissions || [];
    const [value, setValue] = useState(user.permissions || baseline);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    if (user.roleName === 'SUPERADMIN') {
        return (
            <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                <Lock className="mt-0.5 w-4 h-4 shrink-0" />
                <p>Super admins can do everything in Nexus. Their permissions can't be changed.</p>
            </div>
        );
    }
    if (catalogError) return <p className="text-sm text-red-600">{catalogError}</p>;
    if (!catalog) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

    const custom = !sameSet(value, baseline);
    const dirty = !sameSet(value, user.permissions || baseline);
    const role = user.roleLabel || user.roleName;

    const save = async () => {
        setSaving(true);
        setError('');
        setSaved(false);
        try {
            const res = await systemService.setPermissions(user.id, custom ? value : []);
            if (res && res.success === false) throw new Error(res.message);
            setSaved(true);
            onSaved?.(res?.data);
        } catch (err) {
            setError(err.message || 'Failed to save permissions');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-700">What they can do</h4>
                    {custom ? (
                        <>
                            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Set for {user.firstName}</span>
                            {canEdit && (
                                <button onClick={() => setValue(baseline)} className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline">
                                    <RotateCcw className="w-3.5 h-3.5" />Use the role's
                                </button>
                            )}
                        </>
                    ) : (
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">From the {role} role</span>
                    )}
                </div>
                {custom && (
                    <p className="text-sm text-gray-500">Changes to the {role} role won't reach {user.firstName} while they have their own permissions.</p>
                )}
                <PermissionPicker catalog={catalog} value={value} onChange={setValue} baseline={baseline} disabled={!canEdit} />
            </div>
            <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
                <MenuPreview permissions={value} />
                {canEdit && (
                    <button
                        onClick={save}
                        disabled={!dirty || saving}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save permissions
                    </button>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}
                {saved && !dirty && <p className="text-sm text-green-700">Saved. {user.firstName} gets this the next time their session refreshes.</p>}
            </aside>
        </div>
    );
};

export default UserPermissionsTab;
